import { useCallback, useRef, useState } from 'react';
import { FacilityFlowCoreModulesPlanningEntitiesWorkLocation as WorkLocationEnum } from '@/api/generated/models';
import type { WorkLocation } from '@/store/useTimeclockStore';

export interface WorkLocationDialogState {
  isOpen: boolean;
  initial: WorkLocation | null;
  onConfirm: (location: WorkLocation) => void;
  onCancel: () => void;
}

/**
 * The question "Homeoffice oder Büro?" asked before clock-in. `ask` opens the dialog and resolves
 * with the chosen work location, or null when the employee cancelled. Office is preselected — it
 * is the usual case, home office the deliberate choice. Render the dialog with
 * `<WorkLocationDialog {...dialog} />`; useClockStamp decides when to ask.
 */
export function useWorkLocationQuestion() {
  const [isOpen, setIsOpen] = useState(false);
  const settleRef = useRef<((location: WorkLocation | null) => void) | null>(null);

  const ask = useCallback(
    () =>
      new Promise<WorkLocation | null>((resolve) => {
        settleRef.current = resolve;
        setIsOpen(true);
      }),
    [],
  );

  const settle = useCallback((location: WorkLocation | null) => {
    const resolve = settleRef.current;
    settleRef.current = null;
    setIsOpen(false);
    resolve?.(location);
  }, []);

  const dialog: WorkLocationDialogState = {
    isOpen,
    initial: WorkLocationEnum.NUMBER_1,
    onConfirm: settle,
    onCancel: () => settle(null),
  };

  return { ask, dialog };
}
